import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton,
  InputLabel, MenuItem, Paper, Select, Snackbar, Stack, TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AssignmentInd as AssignIcon,
  CheckCircle as ResolveIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Inbox as InboxIcon,
  LockOpen as ReopenIcon,
  Reply as ReplyIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import apiService from '../../services/apiServices';
import { getAuthHeaders, getLogedInUser, getUserDisplayName } from '../../services/authSession';
import { API_URL } from '../../services/backendMode';

// ─── constants ────────────────────────────────────────────────────────────────
const STATUS_OPTS = [
  { value: 'all',      label: 'Të gjitha' },
  { value: 'open',     label: 'Hapur' },
  { value: 'assigned', label: 'Marrë' },
  { value: 'resolved', label: 'Zgjidhur' },
  { value: 'closed',   label: 'Mbyllur' },
];

const STATUS_COLOR = {
  open:     'warning',
  assigned: 'info',
  resolved: 'success',
  closed:   'default',
};

const STATUS_LABEL = {
  open:     'Hapur',
  assigned: 'Marrë',
  resolved: 'Zgjidhur',
  closed:   'Mbyllur',
};

const fmtDate = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleString('sq-AL', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

// ─── sub-component: message bubble ───────────────────────────────────────────
function MsgBubble({ msg }) {
  const inbound = msg.Direction === 'inbound';
  return (
    <Box sx={{ display: 'flex', justifyContent: inbound ? 'flex-start' : 'flex-end', mb: 1.5 }}>
      <Box
        sx={{
          maxWidth: '80%',
          px: 2, py: 1.25,
          borderRadius: 2,
          bgcolor: inbound ? '#F0F9FF' : '#EEF2FF',
          border: '1px solid',
          borderColor: inbound ? '#BAE6FD' : '#C7D2FE',
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          {inbound ? `${msg.FromName || msg.FromEmail} (klient)` : `${msg.FromName || 'Nexuma'}`}
          {' · '}
          {fmtDate(msg.CreatedAt)}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.Body}</Typography>
      </Box>
    </Box>
  );
}

// ─── main component ────────────────────────────────────────────────────────────
export default function TicketsList() {
  const [tickets, setTickets]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText]   = useState('');
  const [detail, setDetail]           = useState(null);   // full ticket with messages
  const [detailOpen, setDetailOpen]   = useState(false);
  const [replyText, setReplyText]     = useState('');
  const [replySending, setReplySending] = useState(false);
  const [createOpen, setCreateOpen]   = useState(false);
  const [inboundOpen, setInboundOpen] = useState(false);
  const [toast, setToast]             = useState({ open: false, msg: '', sev: 'success' });

  // Create form
  const [newForm, setNewForm] = useState({ subject: '', customer_email: '', customer_name: '', notes: '' });
  // Test inbound form
  const [ibForm, setIbForm] = useState({ from_email: '', from_name: '', subject: '', body: '' });

  // Current user (for tagging outbound replies / ownership).
  // NOTE: this used to read localStorage key 'user' — a key nothing ever writes,
  // so the email was always '' and every reply was signed "Nexuma".
  const sessionUser = getLogedInUser();
  const userEmail = (sessionUser?.data?.email || sessionUser?.email || '');
  const userName  = getUserDisplayName('Nexuma');

  const showToast = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  // ── load ────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const r = await fetch(`${API_URL}/api/tickets${params}`, {
        headers: { ...getAuthHeaders(), 'x-user-email': userEmail },
      });
      const json = await r.json();
      setTickets(json.data || []);
    } catch {
      showToast('Gabim në ngarkimin e biletave', 'error');
    }
    setLoading(false);
  }, [statusFilter, userEmail]);

  useEffect(() => { load(); }, [load]);

  // ── open detail ─────────────────────────────────────────────────────────────
  const openDetail = async (t) => {
    try {
      const r = await fetch(`${API_URL}/api/tickets/${t.id || t.Id}`, {
        headers: { ...getAuthHeaders(), 'x-user-email': userEmail },
      });
      const json = await r.json();
      setDetail(json.data);
      setReplyText('');
      setDetailOpen(true);
    } catch {
      showToast('Gabim në ngarkimin e biletës', 'error');
    }
  };

  // ── take ownership ──────────────────────────────────────────────────────────
  const takeOwnership = async () => {
    if (!detail) return;
    // tickets.owner_id is a FK to workers(id), so use the session's worker_id —
    // not the users(id), which would point at an unrelated worker row.
    const workerId = sessionUser?.data?.worker_id || null;
    try {
      const r = await fetch(`${API_URL}/api/tickets/${detail.id || detail.Id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), 'x-user-email': userEmail },
        body: JSON.stringify({ owner_id: workerId, owner_name: userName, status: 'assigned' }),
      });
      const json = await r.json();
      if (json.success) {
        setDetail(prev => ({ ...prev, OwnerId: workerId, OwnerName: userName, Status: 'assigned' }));
        showToast('Keni marrë pronësinë e biletës');
        load();
      }
    } catch { showToast('Gabim', 'error'); }
  };

  // ── status change (resolve / close / reopen) ─────────────────────────────────
  const changeStatus = async (newStatus) => {
    if (!detail) return;
    try {
      const r = await fetch(`${API_URL}/api/tickets/${detail.id || detail.Id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), 'x-user-email': userEmail },
        body: JSON.stringify({ status: newStatus }),
      });
      const json = await r.json();
      if (json.success) {
        setDetail(prev => ({ ...prev, Status: newStatus }));
        showToast(`Statusi ndryshuar → ${STATUS_LABEL[newStatus] || newStatus}`);
        load();
      }
    } catch { showToast('Gabim', 'error'); }
  };

  // ── send reply ───────────────────────────────────────────────────────────────
  const sendReply = async (resolve = false) => {
    if (!replyText.trim() || !detail) return;
    setReplySending(true);
    try {
      const r = await fetch(`${API_URL}/api/tickets/${detail.id || detail.Id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), 'x-user-email': userEmail },
        body: JSON.stringify({
          body:       replyText,
          from_email: userEmail,
          from_name:  userName,
          resolve,
        }),
      });
      const json = await r.json();
      if (json.success) {
        setReplyText('');
        showToast(resolve ? 'Bileta u zgjidh dhe replika u dërgua' : 'Replika u dërgua');
        // Reload detail
        await openDetail(detail);
        load();
      } else { showToast('Gabim gjatë dërgimit', 'error'); }
    } catch { showToast('Gabim', 'error'); }
    setReplySending(false);
  };

  // ── create manual ticket ─────────────────────────────────────────────────────
  const createTicket = async () => {
    if (!newForm.subject.trim() || !newForm.customer_email.trim()) {
      showToast('Subjekti dhe emaili i klientit janë të detyrueshme', 'error');
      return;
    }
    try {
      const r = await fetch(`${API_URL}/api/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), 'x-user-email': userEmail },
        body: JSON.stringify(newForm),
      });
      const json = await r.json();
      if (json.success) {
        setCreateOpen(false);
        setNewForm({ subject: '', customer_email: '', customer_name: '', notes: '' });
        showToast(`Bileta ${json.data.TicketNumber} u krijua`);
        load();
      } else { showToast('Gabim', 'error'); }
    } catch { showToast('Gabim', 'error'); }
  };

  // ── simulate inbound email ───────────────────────────────────────────────────
  const sendInbound = async () => {
    if (!ibForm.from_email.trim() || !ibForm.subject.trim()) {
      showToast('from_email dhe subject janë të detyrueshme', 'error');
      return;
    }
    try {
      // Authenticated call (Bearer token via apiService) — the backend now
      // requires a real admin/manager session or a webhook secret here.
      const json = await apiService().post('tickets/inbound', ibForm);
      if (json.success) {
        setInboundOpen(false);
        setIbForm({ from_email: '', from_name: '', subject: '', body: '' });
        showToast(`Bileta ${json.ticket?.TicketNumber} ${json.isNew ? 'u krijua' : 'u përditësua'}`);
        load();
      } else { showToast(json.error || 'Gabim', 'error'); }
    } catch { showToast('Gabim', 'error'); }
  };

  // ── counters ─────────────────────────────────────────────────────────────────
  const counts = tickets.reduce((a, t) => { a[t.Status] = (a[t.Status] || 0) + 1; return a; }, {});

  // ── filtered list ─────────────────────────────────────────────────────────────
  const q = searchText.trim().toLowerCase();
  const visibleTickets = q
    ? tickets.filter(t =>
        (t.TicketNumber  || '').toLowerCase().includes(q) ||
        (t.Subject       || '').toLowerCase().includes(q) ||
        (t.CustomerEmail || '').toLowerCase().includes(q) ||
        (t.CustomerName  || '').toLowerCase().includes(q) ||
        (t.OwnerName     || '').toLowerCase().includes(q)
      )
    : tickets;

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <InboxIcon sx={{ color: '#6366F1', fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700}>Bileta Nexuma</Typography>
          <Chip label={`${tickets.length} total`} size="small" sx={{ bgcolor: 'primary.soft', color: 'primary.onSoft', fontWeight: 700 }} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
            Rifreskim
          </Button>
          <Button size="small" variant="outlined" startIcon={<EmailIcon />} onClick={() => setInboundOpen(true)}>
            Test Email Hyrës
          </Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Biletë e re
          </Button>
        </Stack>
      </Stack>

      {/* Status summary chips */}
      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
        {STATUS_OPTS.slice(1).map(s => (
          <Chip
            key={s.value}
            label={`${s.label} (${counts[s.value] || 0})`}
            color={STATUS_COLOR[s.value] || 'default'}
            variant={statusFilter === s.value ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter(prev => prev === s.value ? 'all' : s.value)}
            size="small"
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          />
        ))}
      </Stack>

      {/* Filter bar */}
      <Stack direction="row" spacing={1.5} mb={2} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Kërko biletë..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.disabled', fontSize: 18 }} /> }}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {STATUS_OPTS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        {(searchText || statusFilter !== 'all') && (
          <Button size="small" variant="text" onClick={() => { setSearchText(''); setStatusFilter('all'); }}>
            Pastro filtrin
          </Button>
        )}
      </Stack>

      {/* Table */}
      {loading
        ? <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        : visibleTickets.length === 0
          ? (
            <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
              <InboxIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                {q ? `Asnjë biletë për "${searchText}"` : 'Asnjë biletë'}
              </Typography>
            </Paper>
          )
          : (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {visibleTickets.map((t, idx) => (
                <Box key={t.id}>
                  {idx > 0 && <Divider />}
                  <Box
                    onClick={() => openDetail(t)}
                    sx={{
                      px: 2.5, py: 1.5, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 2,
                      flexWrap: 'wrap',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant="caption" color="text.disabled" sx={{ width: 140, flexShrink: 0, fontFamily: 'monospace', fontWeight: 700 }}>
                      {t.TicketNumber}
                    </Typography>
                    <Box flex={1} minWidth={160}>
                      <Typography variant="body2" fontWeight={600} noWrap>{t.Subject}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.CustomerEmail}</Typography>
                    </Box>
                    <Chip
                      label={STATUS_LABEL[t.Status] || t.Status}
                      color={STATUS_COLOR[t.Status] || 'default'}
                      size="small"
                      sx={{ fontWeight: 700, minWidth: 74 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ width: 120, textAlign: 'right', flexShrink: 0 }}>
                      {fmtDate(t.CreatedAt)}
                    </Typography>
                    {t.OwnerName && (
                      <Chip label={t.OwnerName} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                    )}
                  </Box>
                </Box>
              ))}
            </Paper>
          )
      }

      {/* ── Ticket Detail Dialog ─────────────────────────────────────────── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace" fontWeight={700}>
                  {detail.TicketNumber}
                </Typography>
                <Typography variant="h6" fontWeight={700} mt={0.25}>{detail.Subject}</Typography>
              </Box>
              <IconButton onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
              {/* Meta row */}
              <Grid container spacing={1.5} mb={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Klienti</Typography>
                  <Typography variant="body2" fontWeight={600}>{detail.CustomerName || detail.CustomerEmail}</Typography>
                  <Typography variant="caption" color="text.secondary">{detail.CustomerEmail}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box mt={0.25}>
                    <Chip label={STATUS_LABEL[detail.Status] || detail.Status} color={STATUS_COLOR[detail.Status]} size="small" sx={{ fontWeight: 700 }} />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Pronari</Typography>
                  <Typography variant="body2" fontWeight={600}>{detail.OwnerName || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Krijuar</Typography>
                  <Typography variant="body2">{fmtDate(detail.CreatedAt)}</Typography>
                </Grid>
              </Grid>

              {/* Action buttons */}
              <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
                {!detail.OwnerId && (
                  <Button size="small" variant="outlined" startIcon={<AssignIcon />} onClick={takeOwnership}>
                    Merr pronësinë
                  </Button>
                )}
                {!['resolved','closed'].includes(detail.Status) && (
                  <Button size="small" variant="outlined" color="success" startIcon={<ResolveIcon />} onClick={() => changeStatus('resolved')}>
                    Zgjidh
                  </Button>
                )}
                {detail.Status !== 'closed' && (
                  <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon />} onClick={() => changeStatus('closed')}>
                    Mbyll
                  </Button>
                )}
                {['resolved','closed'].includes(detail.Status) && (
                  <Button size="small" variant="outlined" color="warning" startIcon={<ReopenIcon />} onClick={() => changeStatus('open')}>
                    Rihap
                  </Button>
                )}
              </Stack>

              {/* Message thread */}
              <Box sx={{ maxHeight: 340, overflowY: 'auto', p: 1, bgcolor: '#FAFAFA', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 2 }}>
                {(detail.Messages || []).length === 0
                  ? <Typography variant="caption" color="text.secondary" textAlign="center" display="block">Asnjë mesazh akoma</Typography>
                  : (detail.Messages || []).map(m => <MsgBubble key={m.id} msg={m} />)
                }
              </Box>

              {/* Reply box */}
              {!['closed'].includes(detail.Status) && (
                <Box>
                  <TextField
                    fullWidth multiline rows={3} size="small"
                    label="Shkruaj përgjigje…"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    sx={{ mb: 1, '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained" size="small" startIcon={<ReplyIcon />}
                      disabled={!replyText.trim() || replySending}
                      onClick={() => sendReply(false)}
                    >
                      Dërgo
                    </Button>
                    <Button
                      variant="contained" color="success" size="small" startIcon={<ResolveIcon />}
                      disabled={!replyText.trim() || replySending}
                      onClick={() => sendReply(true)}
                    >
                      Dërgo & Zgjidh
                    </Button>
                  </Stack>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* ── Create Manual Ticket Dialog ──────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Biletë e re manuale
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setCreateOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <TextField fullWidth size="small" label="Subjekti *" value={newForm.subject} onChange={e => setNewForm(p => ({ ...p, subject: e.target.value }))} />
            <TextField fullWidth size="small" label="Email klienti *" value={newForm.customer_email} onChange={e => setNewForm(p => ({ ...p, customer_email: e.target.value }))} />
            <TextField fullWidth size="small" label="Emri klienti" value={newForm.customer_name} onChange={e => setNewForm(p => ({ ...p, customer_name: e.target.value }))} />
            <TextField fullWidth size="small" label="Shënime" multiline rows={3} value={newForm.notes} onChange={e => setNewForm(p => ({ ...p, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Anulo</Button>
          <Button variant="contained" onClick={createTicket}>Krijo biletën</Button>
        </DialogActions>
      </Dialog>

      {/* ── Test Inbound Email Dialog ────────────────────────────────────── */}
      <Dialog open={inboundOpen} onClose={() => setInboundOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Simulo email hyrës
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setInboundOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
            Simulon marrjen e një emaili nga një klient. Në prodhim kjo thirret automatikisht nga webhook-u i emailit.
          </Alert>
          <Stack spacing={2} mt={0.5}>
            <TextField fullWidth size="small" label="from_email *" value={ibForm.from_email} onChange={e => setIbForm(p => ({ ...p, from_email: e.target.value }))} />
            <TextField fullWidth size="small" label="from_name" value={ibForm.from_name} onChange={e => setIbForm(p => ({ ...p, from_name: e.target.value }))} />
            <TextField fullWidth size="small" label="subject *" value={ibForm.subject} onChange={e => setIbForm(p => ({ ...p, subject: e.target.value }))} />
            <TextField fullWidth size="small" label="body" multiline rows={4} value={ibForm.body} onChange={e => setIbForm(p => ({ ...p, body: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInboundOpen(false)}>Anulo</Button>
          <Button variant="contained" onClick={sendInbound}>Dërgo</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast(p => ({ ...p, open: false }))}>
        <Alert severity={toast.sev} onClose={() => setToast(p => ({ ...p, open: false }))} sx={{ borderRadius: 1.5 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
